import { EditableElement } from './types';

/**
 * Group multiple elements together
 * Creates a new group element containing the selected elements
 */
export const groupElements = (
  elements: EditableElement[],
  selectedIds: string[]
): { elements: EditableElement[]; groupId: string } => {
  if (selectedIds.length < 2) {
    throw new Error('At least 2 elements must be selected to create a group');
  }

  // Filter selected elements, excluding connectors (they can't be grouped)
  const selectedElements = elements.filter((el) => 
    selectedIds.includes(el.id) && el.type !== 'connector'
  );
  
  if (selectedElements.length < 2) {
    throw new Error('At least 2 non-connector elements must be selected to create a group');
  }
  
  // Calculate bounding box
  const bounds = calculateBounds(selectedElements);
  
  // Create group element
  const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const groupElement: EditableElement = {
    id: groupId,
    type: 'group',
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    data: {
      children: selectedElements.map((el) => ({
        ...el,
        // Store relative position within group
        x: el.x - bounds.x,
        y: el.y - bounds.y,
      })),
    },
  };

  // Remove grouped elements and add group
  const newElements = elements
    .filter((el) => !selectedIds.includes(el.id))
    .concat(groupElement);

  return { elements: newElements, groupId };
};

/**
 * Ungroup a group element
 * Extracts all child elements and adds them back to the canvas
 */
export const ungroupElements = (
  elements: EditableElement[],
  groupId: string
): { elements: EditableElement[]; ungroupedIds: string[] } => {
  const group = elements.find((el) => el.id === groupId);
  
  if (!group || group.type !== 'group' || !group.data?.children) {
    throw new Error('Element is not a valid group');
  }

  // Extract children and convert relative positions to absolute
  const children = (group.data.children as EditableElement[]).map((child) => ({
    ...child,
    x: child.x + group.x,
    y: child.y + group.y,
  }));

  // Remove group and add children
  const newElements = elements
    .filter((el) => el.id !== groupId)
    .concat(children);

  const ungroupedIds = children.map((el) => el.id);

  return { elements: newElements, ungroupedIds };
};

/**
 * Check if an element is a group
 */
export const isGroup = (element: EditableElement): boolean => {
  return element.type === 'group';
};

/**
 * Get all child IDs from a group
 */
export const getGroupChildren = (group: EditableElement): string[] => {
  if (!isGroup(group) || !group.data?.children) {
    return [];
  }

  return (group.data.children as EditableElement[]).map((el) => el.id);
};

/**
 * Calculate bounding box for multiple elements
 */
export const calculateBounds = (
  elements: EditableElement[]
): { x: number; y: number; width: number; height: number } => {
  if (elements.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  elements.forEach((el) => {
    minX = Math.min(minX, el.x);
    minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + el.width);
    maxY = Math.max(maxY, el.y + el.height);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Update group when child positions change
 * Recalculates group bounds and child relative positions
 */
export const updateGroup = (
  group: EditableElement,
  childUpdates: Map<string, { x: number; y: number }>
): EditableElement => {
  if (!isGroup(group) || !group.data?.children) {
    return group;
  }

  // Update child positions
  const updatedChildren = (group.data.children as EditableElement[]).map((child) => {
    const update = childUpdates.get(child.id);
    if (update) {
      return { ...child, ...update };
    }
    return child;
  });

  // Recalculate bounds with absolute positions
  const absoluteChildren = updatedChildren.map((child) => ({
    ...child,
    x: child.x + group.x,
    y: child.y + group.y,
  }));

  const newBounds = calculateBounds(absoluteChildren);

  // Convert back to relative positions
  const relativeChildren = absoluteChildren.map((child) => ({
    ...child,
    x: child.x - newBounds.x,
    y: child.y - newBounds.y,
  }));

  return {
    ...group,
    x: newBounds.x,
    y: newBounds.y,
    width: newBounds.width,
    height: newBounds.height,
    data: {
      ...group.data,
      children: relativeChildren,
    },
  };
};
